"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Vector = void 0;
class Vector {
    constructor(x, y, z) {
        this.x = x || 0;
        this.y = y || 0;
        this.z = z || 0;
    }
    negative() {
        return new Vector(-this.x, -this.y, -this.z);
    }
    add(v) {
        if (v instanceof Vector) {
            return new Vector(this.x + v.x, this.y + v.y, this.z + v.z);
        }
        return new Vector(this.x + v, this.y + v, this.z + v);
    }
    subtract(v) {
        if (v instanceof Vector) {
            return new Vector(this.x - v.x, this.y - v.y, this.z - v.z);
        }
        return new Vector(this.x - v, this.y - v, this.z - v);
    }
    multiply(v) {
        if (v instanceof Vector) {
            return new Vector(this.x * v.x, this.y * v.y, this.z * v.z);
        }
        return new Vector(this.x * v, this.y * v, this.z * v);
    }
    divide(v) {
        if (v instanceof Vector) {
            return new Vector(this.x / v.x, this.y / v.y, this.z / v.z);
        }
        return new Vector(this.x / v, this.y / v, this.z / v);
    }
    equals(v) {
        return this.x === v.x && this.y === v.y && this.z === v.z;
    }
    dot(v) {
        return this.x * v.x + this.y * v.y + this.z * v.z;
    }
    cross(v) {
        return new Vector(this.y * v.z - this.z * v.y, this.z * v.x - this.x * v.z, this.x * v.y - this.y * v.x);
    }
    length() {
        return Math.sqrt(this.dot(this));
    }
    unit() {
        return this.divide(this.length());
    }
    min() {
        return Math.min(Math.min(this.x, this.y), this.z);
    }
    max() {
        return Math.max(Math.max(this.x, this.y), this.z);
    }
    toAngles() {
        return {
            theta: Math.atan2(this.z, this.x),
            phi: Math.asin(this.y / this.length()),
        };
    }
    round(factor = 100) {
        var vect = this.clone();
        if (vect.x !== 0) {
            vect.x = Math.round(vect.x * factor) / factor;
        }
        if (vect.y !== 0) {
            vect.y = Math.round(vect.y * factor) / factor;
        }
        if (vect.z !== 0 && vect.z != null) {
            vect.z = Math.round(vect.z * factor) / factor;
        }
        return vect;
    }
    norm() {
        var pow = Math.pow(this.x, 2) + Math.pow(this.y, 2) + Math.pow(this.z || 0, 2);
        return Math.sqrt(pow);
    }
    normalize() {
        var norm = this.norm();
        if (norm > 1e-12) {
            return new Vector(this.x / norm, this.y / norm, this.z / norm);
        }
        return this.clone();
    }
    angleTo(a) {
        return Math.acos(this.dot(a) / (this.length() * a.length()));
    }
    toArray(n = 3) {
        return [this.x, this.y, this.z].slice(0, n || 3);
    }
    clone() {
        return new Vector(this.x, this.y, this.z);
    }
    init(x, y, z) {
        this.x = x;
        this.y = y;
        this.z = z;
        return this;
    }
    static create(obj) {
        if (obj instanceof Vector) {
            return obj;
        }
        if (Array.isArray(obj)) {
            return new Vector(obj[0], obj[1], obj[2]);
        }
        return new Vector(obj.x, obj.y, obj.z);
    }
    /**
     * Makes a (x,y,z) Cartesian vector out of length, bearing and pitch
     * <p/>
     * As per Spike tests
     * y point North, x points East, z point Zenith
     *
     * @param length  in meters
     * @param bearing East (bearing) azimuth in radian (East is pi/2)
     * @param pitch   in radians (Zenith is pi/2)
     * @return Cartesian (x,y,z) vector
     */
    static distanceBearingPitchToCartesian(length, bearing, pitch) {
        var yxProjection = length * Math.cos(pitch);
        return new Vector(yxProjection * Math.sin(bearing), yxProjection * Math.cos(bearing), length * Math.sin(pitch));
    }
    /** Makes a triplet (length, bearing, pitch) out of a a (x,y,z) Cartesian vector
     *
     * @param w (x,y,z) Cartesian vector
     * @return (length, bearing, pitch) representation
     */
    static distanceBearingAndPitchFromCartesian(w) {
        var length = w.norm();
        var pitch = 0.0;
        var bearing = 0.0;
        if (length > 1e-12) {
            pitch = Math.asin(w.z / length);
            if (w.y !== 0) {
                bearing = Math.atan2(w.x, w.y);
            }
            else {
                if (w.x > 0) {
                    bearing = Math.PI / 2.0;
                }
                else if (w.x < 0) {
                    bearing = -Math.PI / 2.0;
                }
            }
        }
        return new Vector(length, bearing, pitch);
    }
    toString() {
        return '(' + this.x + ', ' + this.y + ', ' + this.z + ')';
    }
    toXYString() {
        return this.x + ', ' + this.y;
    }
    inverseRotate(rotation) {
        return new Vector(this.x * rotation[0] + this.y * rotation[3] + this.z * rotation[6], this.x * rotation[1] + this.y * rotation[4] + this.z * rotation[7], this.x * rotation[2] + this.y * rotation[5] + this.z * rotation[8]);
    }
    rotate(rotation) {
        return new Vector(this.x * rotation[0] + this.y * rotation[1] + this.z * rotation[2], this.x * rotation[3] + this.y * rotation[4] + this.z * rotation[5], this.x * rotation[6] + this.y * rotation[7] + this.z * rotation[8]);
    }
    approx(v) {
        return approx(this.x, v.x) && approx(this.y, v.y) && approx(this.z, v.z);
    }
}
exports.Vector = Vector;
function approx(a, b) {
    return Math.abs(a - b) < 1e-12;
}
//# sourceMappingURL=vector.js.map